/**
 * Narrow shape required to evaluate lock semantics. Works for both TemplateSigningField
 * and the DocumentPreview PreviewField (which widens fieldType to `string`).
 */
interface LockableField {
  fieldType: string;
  defaultValue?: string | null;
  isReadOnly?: boolean;
}

/**
 * A field has a "meaningful" default when it could be stamped onto the final PDF.
 * CHECKBOX stores 'true'/'false' strings; other types use any non-empty trimmed string.
 */
export function hasDefaultValue(field: Pick<LockableField, 'fieldType' | 'defaultValue'>): boolean {
  if (field.fieldType === 'CHECKBOX') {
    return field.defaultValue === 'true' || field.defaultValue === 'false';
  }
  return (field.defaultValue ?? '').toString().trim() !== '';
}

/**
 * `isReadOnly` is only meaningful paired with a default — an empty default locked
 * to read-only would silently stamp nothing at sign-time. Use this everywhere the
 * UI reflects the lock state so a stale `{ isReadOnly: true, defaultValue: '' }`
 * record can't surface a lock the user can't see or uncheck.
 */
export function isEffectivelyLocked(field: LockableField): boolean {
  return !!field.isReadOnly && hasDefaultValue(field);
}
